import { LabelSelection, LabelSummary } from '@/analysis/types';

const without = (ids: number[], id: number): number[] => ids.filter(i => i !== id);

export const emptySelection = (): LabelSelection => ({
  labelIds: [],
  labelCategoryIds: [],
  excludedLabelIds: [],
});

export const isEmpty = (selection: LabelSelection): boolean => (
  !selection.labelIds.length && !selection.labelCategoryIds.length
);

export const countSelected = (selection: LabelSelection): number => (
  selection.labelIds.length + selection.labelCategoryIds.length
);

export const isCategorySelected = (
  selection: LabelSelection,
  categoryId: number,
): boolean => selection.labelCategoryIds.includes(categoryId);

export const isLabelExcluded = (
  selection: LabelSelection,
  label: LabelSummary,
): boolean => selection.excludedLabelIds.includes(label.id);

/**
 * A label in a selected category is on unless it was explicitly excluded.
 */
export const isLabelSelected = (
  selection: LabelSelection,
  label: LabelSummary,
): boolean => (
  isCategorySelected(selection, label.categoryId)
    ? !isLabelExcluded(selection, label)
    : selection.labelIds.includes(label.id)
);

export const toggleLabel = (
  selection: LabelSelection,
  label: LabelSummary,
): LabelSelection => {
  if (isCategorySelected(selection, label.categoryId)) {
    // The category already implies this label, so the only way to turn it off
    // is to exclude it.
    return {
      ...selection,
      labelIds: without(selection.labelIds, label.id),
      excludedLabelIds: isLabelExcluded(selection, label)
        ? without(selection.excludedLabelIds, label.id)
        : [...selection.excludedLabelIds, label.id],
    };
  }

  return {
    ...selection,
    labelIds: selection.labelIds.includes(label.id)
      ? without(selection.labelIds, label.id)
      : [...selection.labelIds, label.id],
    excludedLabelIds: without(selection.excludedLabelIds, label.id),
  };
};

export const toggleCategory = (
  selection: LabelSelection,
  categoryId: number,
  categoryLabelIds: number[],
): LabelSelection => {
  if (isCategorySelected(selection, categoryId)) {
    const labelCategoryIds = without(selection.labelCategoryIds, categoryId);

    return {
      ...selection,
      labelCategoryIds,
      // An exclusion only means something inside a selected category, so drop
      // the ones this category leaves behind.
      excludedLabelIds: labelCategoryIds.length
        ? selection.excludedLabelIds.filter(id => !categoryLabelIds.includes(id))
        : [],
    };
  }

  return {
    labelCategoryIds: [...selection.labelCategoryIds, categoryId],
    // The category now implies its labels, so individual picks are redundant.
    labelIds: selection.labelIds.filter(id => !categoryLabelIds.includes(id)),
    excludedLabelIds: selection.excludedLabelIds.filter(id => !categoryLabelIds.includes(id)),
  };
};
